'use client';
import { Box, Typography, Button, Container, Card, CardContent, Grid, Paper, Chip, LinearProgress } from '@mui/material';
import { Shield, AccessTime, Star, TrendingUp, Speed, CheckCircle, Psychology, Business, Assessment, Settings, Security, Analytics, SmartToy, Hub, Person } from '@mui/icons-material';
import Header from '../components/HomepageContent/Header';
import Footer from '../components/HomepageContent/Footer';
import Link from 'next/link';

export default function Features() {
  const pillars = [
    {
      title: "QAPI Management",
      description: "Comprehensive quality assurance and performance improvement tools that streamline compliance and enhance resident care outcomes.",
      icon: Assessment,
      features: ["Automated F-tag mapping", "Real-time quality metrics", "Performance tracking", "Compliance alerts"],
      impact: 85,
      color: "#153C7F"
    },
    {
      title: "Workflow Automation",
      description: "Intelligent automation that reduces manual tasks and eliminates errors while maintaining the human touch in care delivery.",
      icon: Settings,
      features: ["Smart form filling", "Task automation", "Process optimization", "Workflow analytics"],
      impact: 90,
      color: "#00B4D8"
    },
    {
      title: "Compliance Management",
      description: "Stay ahead of regulatory requirements with automated tracking, deadline management, and state-specific compliance tools.",
      icon: Security,
      features: ["F-tag monitoring", "Deadline alerts", "State compliance", "Audit preparation"],
      impact: 95,
      color: "#28A745"
    },
    {
      title: "Resident Care Planning",
      description: "Person-centered care planning that adapts to individual needs and ensures comprehensive care coordination.",
      icon: Person,
      features: ["Individualized care plans", "Care coordination", "Outcome tracking", "Family communication"],
      impact: 88,
      color: "#6F42C1"
    }
  ];

  const platformFeatures = [
    {
      icon: Analytics,
      title: "Advanced Analytics",
      description: "Data-driven insights for better decision making and continuous improvement",
      benefits: ["Real-time dashboards", "Trend analysis", "Predictive insights", "Performance metrics"]
    },
    {
      icon: SmartToy,
      title: "AI-Powered Automation",
      description: "Intelligent automation that learns and adapts to your facility's unique needs",
      benefits: ["Smart workflows", "Predictive alerts", "Automated reporting", "Intelligent recommendations"]
    },
    {
      icon: Hub,
      title: "Seamless Integration",
      description: "Connect with existing systems and workflows for a unified experience",
      benefits: ["API connectivity", "Data synchronization", "Third-party integrations", "Custom workflows"]
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      <Header />

      {/* Hero Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #153C7F 0%, #1E4A8C 50%, #2A5A9C 100%)',
        py: 12, 
        px: 4,
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: 'radial-gradient(circle at 25% 25%, white 2px, transparent 2px), radial-gradient(circle at 75% 75%, white 2px, transparent 2px)',
          backgroundSize: '50px 50px'
        }} />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography 
            variant="h1" 
            sx={{ 
              color: 'white', 
              mb: 4,
              fontWeight: 800,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}
          >
            Comprehensive Platform Features
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 6,
              color: 'rgba(255,255,255,0.9)',
              maxWidth: '900px',
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: '1.2rem', md: '1.5rem' }
            }}
          >
            Discover the powerful tools and capabilities that make CareAscendium the leading solution for skilled nursing facilities.
          </Typography>
        </Container>
      </Box>

      {/* Pillars Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" sx={{ color: '#153C7F', mb: 4, fontWeight: 700 }}>
            Core Platform Pillars
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: '800px', mx: 'auto', lineHeight: 1.6, color: '#333', fontSize: '1.1rem' }}>
            Four essential pillars that work together to transform your facility operations
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {pillars.map((pillar, index) => (
            <Grid size={{ xs: 12, md: 6 }} key={index}>
              <Paper 
                elevation={8}
                sx={{ 
                  p: 4,
                  height: '100%',
                  borderRadius: 3,
                  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                  border: `1px solid ${pillar.color}20`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 12px 32px ${pillar.color}30`
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    background: `linear-gradient(45deg, ${pillar.color}, ${pillar.color}80)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 3,
                    boxShadow: `0 4px 15px ${pillar.color}40`
                  }}>
                    <pillar.icon sx={{ fontSize: 28, color: 'white' }} />
                  </Box>
                  <Typography variant="h3" sx={{ color: pillar.color, fontWeight: 700 }}>
                    {pillar.title}
                  </Typography>
                </Box>
                
                <Typography variant="body1" sx={{ color: '#666', mb: 3, lineHeight: 1.6, fontSize: '1rem' }}>
                  {pillar.description}
                </Typography>
                
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: pillar.color, fontWeight: 600 }}>
                      Impact Score
                    </Typography>
                    <Typography variant="body2" sx={{ color: pillar.color, fontWeight: 700 }}>
                      {pillar.impact}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={pillar.impact} 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      bgcolor: `${pillar.color}20`,
                      '& .MuiLinearProgress-bar': {
                        bgcolor: pillar.color,
                        borderRadius: 4
                      }
                    }} 
                  />
                </Box>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {pillar.features.map((feature, idx) => (
                    <Chip 
                      key={idx}
                      label={feature} 
                      size="small" 
                      sx={{ 
                        bgcolor: `${pillar.color}15`, 
                        color: pillar.color,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        border: `1px solid ${pillar.color}30`
                      }} 
                    />
                  ))}
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Platform Features Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)',
        py: 10 
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" sx={{ color: '#153C7F', mb: 4, fontWeight: 700 }}>
              Advanced Platform Features
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: '800px', mx: 'auto', lineHeight: 1.6, color: '#333', fontSize: '1.1rem' }}>
              Cutting-edge technology that sets CareAscendium apart from traditional solutions
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {platformFeatures.map((feature, index) => (
              <Grid size={{ xs: 12, md: 4 }} key={index}>
                <Paper 
                  elevation={6}
                  sx={{ 
                    p: 4,
                    textAlign: 'center',
                    height: '100%',
                    borderRadius: 3,
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                    border: '1px solid rgba(21,60,127,0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 32px rgba(21,60,127,0.15)'
                    }
                  }}
                >
                  <Box sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'linear-gradient(45deg, #153C7F, #1E4A8C)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                    boxShadow: '0 4px 15px rgba(21,60,127,0.3)'
                  }}>
                    <feature.icon sx={{ fontSize: 40, color: 'white' }} />
                  </Box>
                  
                  <Typography variant="h4" sx={{ color: '#153C7F', mb: 2, fontWeight: 700 }}>
                    {feature.title}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ color: '#666', mb: 3, lineHeight: 1.6 }}>
                    {feature.description}
                  </Typography>
                  
                  <Box sx={{ textAlign: 'left' }}>
                    {feature.benefits.map((benefit, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ 
                          width: 6, 
                          height: 6, 
                          borderRadius: '50%', 
                          bgcolor: '#153C7F', 
                          mr: 2 
                        }} />
                        <Typography variant="body2" sx={{ color: '#666', fontSize: '0.9rem' }}>
                          {benefit}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Integration Benefits Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" sx={{ color: '#153C7F', mb: 4, fontWeight: 700 }}>
            Integration Benefits
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: '800px', mx: 'auto', lineHeight: 1.6, color: '#333', fontSize: '1.1rem' }}>
            Seamlessly integrate with your existing systems and workflows
          </Typography>
        </Box>

        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper 
              elevation={6}
              sx={{ 
                p: 4,
                textAlign: 'center',
                height: '100%',
                borderRadius: 3,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid rgba(21,60,127,0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(21,60,127,0.15)'
                }
              }}
            >
              <Box sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                background: 'linear-gradient(45deg, #00B4D8, #0099CC)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
                boxShadow: '0 4px 15px rgba(0,180,216,0.3)'
              }}>
                <SmartToy sx={{ fontSize: 30, color: 'white' }} />
              </Box>
              
              <Typography variant="h4" sx={{ color: '#00B4D8', mb: 2, fontWeight: 700 }}>
                AI-Powered
              </Typography>
              
              <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.6 }}>
                Advanced artificial intelligence that learns from your data and adapts to your facility's unique needs and workflows.
              </Typography>
            </Paper>
          </Grid>
          
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper 
              elevation={6}
              sx={{ 
                p: 4,
                textAlign: 'center',
                height: '100%',
                borderRadius: 3,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid rgba(21,60,127,0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(21,60,127,0.15)'
                }
              }}
            >
              <Box sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                background: 'linear-gradient(45deg, #28A745, #20C997)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
                boxShadow: '0 4px 15px rgba(40,167,69,0.3)'
              }}>
                <Hub sx={{ fontSize: 30, color: 'white' }} />
              </Box>
              
              <Typography variant="h4" sx={{ color: '#28A745', mb: 2, fontWeight: 700 }}>
                Unified Platform
              </Typography>
              
              <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.6 }}>
                All your facility management needs in one integrated platform, eliminating the need for multiple disconnected systems.
              </Typography>
            </Paper>
          </Grid>
          
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper 
              elevation={6}
              sx={{ 
                p: 4,
                textAlign: 'center',
                height: '100%',
                borderRadius: 3,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid rgba(21,60,127,0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(21,60,127,0.15)'
                }
              }}
            >
              <Box sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                background: 'linear-gradient(45deg, #6F42C1, #8B5CF6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
                boxShadow: '0 4px 15px rgba(111,66,193,0.3)'
              }}>
                <Analytics sx={{ fontSize: 30, color: 'white' }} />
              </Box>
              
              <Typography variant="h4" sx={{ color: '#6F42C1', mb: 2, fontWeight: 700 }}>
                Data-Driven
              </Typography>
              
              <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.6 }}>
                Comprehensive analytics and reporting that provide actionable insights for continuous improvement and better decision-making.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ 
        py: 12, 
        background: 'linear-gradient(135deg, #153C7F 0%, #1E4A8C 100%)',
        color: 'white',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: 'radial-gradient(circle at 25% 25%, white 2px, transparent 2px), radial-gradient(circle at 75% 75%, white 2px, transparent 2px)',
          backgroundSize: '50px 50px'
        }} />
        
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h2" sx={{ mb: 4, fontWeight: 800, fontSize: { xs: '2rem', md: '3rem' } }}>
            Experience the Power
          </Typography>
          <Typography variant="h5" sx={{ mb: 6, opacity: 0.9, lineHeight: 1.6 }}>
            See how CareAscendium's comprehensive features can transform your facility operations
          </Typography>
          
          <Link href="/contact" style={{ textDecoration: 'none' }}>
            <Button 
              variant="contained" 
              size="large"
              startIcon={<Assessment />}
              sx={{ 
                bgcolor: 'white',
                color: '#153C7F',
                px: 6,
                py: 3,
                fontSize: '1.2rem',
                fontWeight: 700,
                borderRadius: 3,
                textTransform: 'none',
                boxShadow: '0 4px 15px rgba(255,255,255,0.3)',
                '&:hover': {
                  bgcolor: '#f5f5f5',
                  boxShadow: '0 6px 20px rgba(255,255,255,0.4)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Request a Demo
            </Button>
          </Link>
        </Container>
      </Box>
      <Footer />
    </Box>
  );
} 